import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Activity, 
  Eye, 
  EyeOff, 
  Loader2, 
  Brain, 
  Shield, 
  Zap,
  CheckCircle2,
  ArrowRight,
  Users,
  Crown,
  Wrench,
  BarChart3
} from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 获取重定向目标，默认为首页
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  // 预设的测试账户
  const testAccounts = [
    {
      email: 'admin@jinjia.com',
      name: '总监',
      role: '管理层',
      icon: Crown,
      color: 'from-purple-500 to-purple-600',
      description: '系统管理员，拥有所有权限'
    },
    {
      email: 'engineer@jinjia.com', 
      name: '李工程师',
      role: '技术部',
      icon: Wrench,
      color: 'from-blue-500 to-blue-600',
      description: '技术工程师，负责系统维护'
    },
    {
      email: 'analyst@jinjia.com',
      name: '王分析师', 
      role: '数据分析部',
      icon: BarChart3,
      color: 'from-green-500 to-green-600',
      description: '数据分析师，专注业务洞察'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        // 登录成功，重定向到目标页面
        navigate(from, { replace: true });
      } else {
        setError(result.message || '登录失败');
      }
    } catch (err) {
      setError('登录过程中发生错误，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickLogin = (accountEmail: string) => {
    setEmail(accountEmail);
    setPassword('123456'); // 模拟密码
    
    // 自动执行登录
    setTimeout(async () => {
      setIsLoading(true);
      try {
        const result = await login(accountEmail, '123456');
        if (result.success) {
          navigate(from, { replace: true });
        }
      } catch (err) {
        setError('快速登录失败');
      } finally {
        setIsLoading(false);
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        
        {/* 左侧 - 品牌展示 */}
        <div className="hidden lg:flex flex-col justify-center space-y-8 px-8">
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Activity className="h-12 w-12 text-blue-600" />
                <div className="absolute inset-0 h-12 w-12 bg-blue-600/20 rounded-full blur-xl"></div>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">智能报表中心</h1>
                <p className="text-slate-600">Industrial Intelligence Platform</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-slate-800">
                数据驱动，智能决策
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                集成AI分析、实时监控、预测分析于一体的工业智能平台，
                为制造业提供全方位的数据洞察和决策支持。
              </p>
            </div>
          </div>

          {/* 功能特色 */}
          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center space-x-3 p-4 bg-white/60 rounded-xl backdrop-blur-sm">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Brain className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">AI智能分析</h3>
                <p className="text-sm text-slate-600">机器学习驱动的业务洞察</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-4 bg-white/60 rounded-xl backdrop-blur-sm">
              <div className="p-2 bg-green-100 rounded-lg">
                <Activity className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">实时监控</h3>
                <p className="text-sm text-slate-600">7x24小时业务状态监控</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-4 bg-white/60 rounded-xl backdrop-blur-sm">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">安全可靠</h3>
                <p className="text-sm text-slate-600">企业级安全保障</p>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧 - 登录表单 */}
        <div className="flex flex-col space-y-6">
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-md">
            <CardHeader className="space-y-2 text-center">
              <CardTitle className="text-2xl">欢迎回来</CardTitle>
              <CardDescription>
                登录您的账户以访问智能报表中心
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-700">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">邮箱地址</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="请输入您的邮箱地址"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">密码</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="请输入您的密码"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-11 w-11 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-500" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      登录中...
                    </>
                  ) : (
                    <>
                      登录
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* 快速登录选项 */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Zap className="h-5 w-5 mr-2 text-orange-500" />
                快速登录 (测试账户)
              </CardTitle>
              <CardDescription>
                点击下方账户即可快速体验系统功能
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <div className="grid gap-3">
                {testAccounts.map((account) => (
                  <Button
                    key={account.email}
                    variant="outline"
                    className="h-auto p-4 justify-start hover:bg-slate-50 border-slate-200"
                    onClick={() => handleQuickLogin(account.email)}
                    disabled={isLoading}
                  >
                    <div className="flex items-center space-x-3 w-full">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${account.color}`}>
                        <account.icon className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-slate-900">{account.name}</div>
                        <div className="text-sm text-slate-500">{account.description}</div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {account.role}
                      </Badge>
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 系统状态 */}
          <div className="flex items-center justify-center space-x-2 text-sm text-slate-600">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>系统运行正常</span>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
} 